<template>
  <UTooltip v-if="tooltip" :text="tooltip" :delay-duration="0">
    <button
      v-bind="$attrs"
      :type="type"
      :class="buttonClasses"
      :disabled="disabled"
      @click="$emit('click', $event)"
    >
      <slot name="icon"></slot>
      <slot></slot>
    </button>
  </UTooltip>
  <button
    v-else
    v-bind="$attrs"
    :type="type"
    :class="buttonClasses"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <slot name="icon"></slot>
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import type { ButtonRounded, ButtonSize, ButtonType, ButtonVariant, ButtonColor } from '@/types';
import { computed } from 'vue';

defineOptions({ inheritAttrs: false });

defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

// Define props with default values
const props = withDefaults(defineProps<{
  color?: ButtonColor
  variant?: ButtonVariant
  size?: ButtonSize
  type?: ButtonType
  rounded?: ButtonRounded
  disabled?: boolean
  className?: string
  tooltip?: string
  square?: boolean
}>(), {
  color: 'primary',
  variant: 'flat',
  size: 'md',
  type: 'button',
  rounded: 'lg',
  disabled: false,
  className: '',
  tooltip: '',
  square: false
})

const variantClasses = computed(() => ({
  flat: props.color === 'primary' && 'bg-primary text-text-primary hover:bg-primary/90' ||
        props.color === 'secondary' && 'bg-secondary-500 hover:bg-secondary-500/90' ||
        props.color === 'surface' && 'bg-surface text-text-surface hover:bg-surface-hover' ||
        props.color === 'error' && 'bg-error hover:bg-error/90 text-white',
  outlined: props.color === 'primary' && 'border border-primary text-text-primary hover:bg-primary/10' ||
           props.color === 'secondary' && 'border border-secondary-500 text-secondary-500 hover:bg-secondary-500/10' ||
           props.color === 'surface' && 'border border-surface text-surface hover:bg-surface-hover' ||
           props.color === 'error' && 'border border-error text-error hover:bg-error/10',
  text: props.color === 'primary' && 'text-text-primary hover:bg-primary/10' ||
        props.color === 'secondary' && 'text-secondary-500 hover:bg-secondary-500/10' ||
        props.color === 'surface' && 'text-surface hover:bg-surface-hover' ||
        props.color === 'error' && 'text-error hover:bg-error/10',
}));

const buttonClasses = computed(() => [
  'flex items-center gap-2 transition-colors',
  // Rounded corners
  props.rounded === 'none' && 'rounded-none',
  props.rounded === 'sm' && 'rounded-sm',
  props.rounded === 'md' && 'rounded-md',
  props.rounded === 'lg' && 'rounded-lg',
  props.rounded === 'xl' && 'rounded-xl',
  props.rounded === 'full' && 'rounded-full',
  // Button variants
  variantClasses.value[props.variant],
  // Button sizes
  props.size === 'sm' && 'p-1.5 text-sm',
  props.size === 'md' && 'p-2',
  props.size === 'lg' && 'p-3 text-lg',
  props.size === 'xl' && 'p-4 text-lg',
  // Square button (for icons)
  props.square && 'p-2 aspect-square',
  // Disabled state
  props.disabled && 'opacity-50 cursor-not-allowed',
  // Custom class
  props.className
]);
</script>
